import { Nullable } from "@core/domain/types/nullable.type";
import {
    CreateExpenseAllTypes,
    CreateInvestmentExpense,
    CreateMaintenanceExpense,
    CreateManagementExpense,
    CreateOtherSubsidizableExpense,
    CreatePersonnelExpense,
    CreateTravelExpense,
} from "@project/domain/models/expenses/create-expense.model";
import { ExpenseTypeEnum } from "@project/domain/models/expenses/expense-type.model";
import { ExpenseAccommodationFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/accommodation/expense-accommodation-form.component";
import { ExpensesFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/expense-modal-body.component";
import { ExpenseInvestmentFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/investment/expense-investment-form.component";
import { ExpenseMaintenanceFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/maintenance/expense-maintenance-form.component";
import { ExpenseManagementFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/management/expense-management-form.component";
import { ExpensePersonnelFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/personnel/expense-personnel-form.component";
import { ExpenseTravelFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/travels/expense-travel-form.component";
import { inject, injectable } from "inversify";
import { CreateExpenseAllTypesBody } from "../../dto/expenses/create-expense.body";
import { InvestmentExpenseMapper } from "./investment-expense.mapper";
import { MaintenanceExpenseMapper } from "./maintenance-expense.mapper";
import { ManagementExpenseMapper } from "./management-expense.mapper";
import { PersonnelExpenseMapper } from "./personnel-expense.mapper";
import { TravelExpenseMapper } from "./travel-expense.mapper";
import { ExpenseOtherSubsidizableFormValuesValidated } from "@project/presentation/components/project-form/expenses/expenses-form/otherSubsidizable/expense-otherSubsidizable-form.component";
import { OtherSubsidizableExpenseMapper } from "./otherSubsidizable-expense.mapper";

@injectable()
export class CreateExpenseMapper {
    constructor(
        @inject(InvestmentExpenseMapper)
        private readonly investmentExpenseMapper: InvestmentExpenseMapper,
        @inject(MaintenanceExpenseMapper)
        private readonly maintenanceExpenseMapper: MaintenanceExpenseMapper,
        @inject(TravelExpenseMapper)
        private readonly travelExpenseMapper: TravelExpenseMapper,
        @inject(ManagementExpenseMapper)
        private readonly managementExpenseMapper: ManagementExpenseMapper,
        @inject(PersonnelExpenseMapper)
        private readonly personnelExpenseMapper: PersonnelExpenseMapper,
        @inject(OtherSubsidizableExpenseMapper)
        private readonly otherSubsidizableExpenseMapper: OtherSubsidizableExpenseMapper,
    ) { }

    mapFromFormValues(
        createFormValues: ExpensesFormValuesValidated,
        projectId: number,
        projectFinantialEntityId?: number | null,
    ): Nullable<CreateExpenseAllTypes> {
        switch (createFormValues.__type) {
            case "INVESTMENT":
                return this.investmentExpenseMapper.mapFromFormValues(
                    createFormValues as ExpenseInvestmentFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            case "MAINTENANCE_ACTIVITIES":
                return this.maintenanceExpenseMapper.mapFromFormValues(
                    createFormValues as ExpenseMaintenanceFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            case "MANAGEMENT_ADMINISTRATION":
                return this.managementExpenseMapper.mapFromFormValues(
                    createFormValues as ExpenseManagementFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            case "TRAVEL":
                return this.travelExpenseMapper.mapFromFormValues(
                    createFormValues as ExpenseTravelFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            case "ACCOMMODATION_MEALS":
                // Handle accommodation forms using the travel mapper
                return this.travelExpenseMapper.mapFromAccommodationFormValues(
                    createFormValues as ExpenseAccommodationFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            case "PERSONNEL_MANAGEMENT_ADMINISTRATION":
                return this.personnelExpenseMapper.mapFromFormValues(
                    createFormValues as ExpensePersonnelFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            case "OTHER_SUBSIDIZABLE_EXPENSES":
                return this.otherSubsidizableExpenseMapper.mapFromFormValues(
                    createFormValues as ExpenseOtherSubsidizableFormValuesValidated,
                    projectId,
                    projectFinantialEntityId,
                );
            default:
                return null;
        }
    }

    mapToDto(
        createExpense: CreateExpenseAllTypes,
    ): Nullable<CreateExpenseAllTypesBody> {
        switch (createExpense.typeId) {
            case ExpenseTypeEnum.INVESTMENT:
                return this.investmentExpenseMapper.mapToDto(
                    createExpense as CreateInvestmentExpense,
                );
            case ExpenseTypeEnum.MAINTENANCE_ACTIVITIES:
                return this.maintenanceExpenseMapper.mapToDto(
                    createExpense as CreateMaintenanceExpense,
                );
            case ExpenseTypeEnum.MANAGEMENT_ADMINISTRATION:
                return this.managementExpenseMapper.mapToDto(
                    createExpense as CreateManagementExpense,
                );
            case ExpenseTypeEnum.TRAVEL:
                return this.travelExpenseMapper.mapToDto(
                    createExpense as CreateTravelExpense,
                );
            case ExpenseTypeEnum.PERSONNEL_MANAGEMENT_ADMINISTRATION:
                return this.personnelExpenseMapper.mapToDto(
                    createExpense as CreatePersonnelExpense,
                );
            case ExpenseTypeEnum.OTHER_SUBSIDIZABLE_EXPENSES:
                return this.otherSubsidizableExpenseMapper.mapToDto(
                    createExpense as CreateOtherSubsidizableExpense,
                );
            default:
                return null;
        }
    }
}
